/**
 * ZoneOverridesCard.jsx
 *
 * Per-zone soil-moisture override editor — Phase B of Per-Zone Architecture.
 * Lets a user say "Rachio zone 1 is a planter, watch WH51 CH4, trigger at 30% MAD"
 * so a single dry planter can fire its own irrigation independent of the lawn
 * average DRY probe.
 *
 * Reads:  GET  /api/sensors/zone-config + GET /api/rachio/zones (optional)
 * Writes: POST /api/sensors/zone-config
 * Wipe:   DELETE /api/sensors/zone-config/{rachio_zone_id}
 */
import React, { useEffect, useMemo, useState } from 'react';
import axios from 'axios';
import { toast } from 'sonner';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from './ui/card';
import { Button } from './ui/button';
import { Input } from './ui/input';
import { Label } from './ui/label';
import { Badge } from './ui/badge';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from './ui/select';
import { API_URL } from '../config/api';
import { Trees, Flower2, Sprout, Trash2, Plus } from 'lucide-react';

const ZONE_TYPES = [
  { value: 'turf', label: 'Turf', icon: Sprout, hint: 'Default lawn — uses schedule MAD' },
  { value: 'planter', label: 'Planter', icon: Flower2, hint: 'Dries fast → triggers at 30% depletion' },
  { value: 'ornamental', label: 'Ornamental', icon: Trees, hint: 'Drought tolerant → triggers at 50% depletion' },
];

const PROBE_CHANNELS = [
  { value: '__none__', label: '— None (use lawn average) —' },
  { value: 'wh51_ch1', label: 'WH51 CH1' },
  { value: 'wh51_ch2', label: 'WH51 CH2' },
  { value: 'wh51_ch3', label: 'WH51 CH3' },
  { value: 'wh51_ch4', label: 'WH51 CH4 (planter probe)' },
];

const ZoneOverridesCard = () => {
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [overrides, setOverrides] = useState({}); // { rachio_zone_id: {...} }
  const [rachioZones, setRachioZones] = useState([]); // [{id, name}]
  const [newZoneId, setNewZoneId] = useState('');

  const fetchAll = async () => {
    try {
      setLoading(true);
      const [{ data: cfg }, zonesResp] = await Promise.all([
        axios.get(`${API_URL}/api/sensors/zone-config`, { withCredentials: true }),
        axios
          .get(`${API_URL}/api/rachio/zones`, { withCredentials: true })
          .catch(() => ({ data: null })),
      ]);
      setOverrides(cfg.zone_overrides || {});
      const dev = zonesResp?.data?.device;
      const zones = (dev?.zones || []).filter((z) => z?.enabled !== false);
      setRachioZones(zones.map((z) => ({ id: z.id, name: z.name || `Zone ${z.zoneNumber || ''}` })));
    } catch (e) {
      toast.error('Could not load zone config.');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchAll();
  }, []);

  const updateOverride = (zid, field, value) => {
    setOverrides((prev) => ({
      ...prev,
      [zid]: { ...(prev[zid] || {}), [field]: value },
    }));
  };

  const addZone = () => {
    const zid = (newZoneId || '').trim();
    if (!zid) {
      toast.error('Pick or enter a Rachio zone ID first.');
      return;
    }
    if (overrides[zid]) {
      toast.info('That zone already has an override below.');
      return;
    }
    setOverrides((prev) => ({
      ...prev,
      [zid]: { zone_type: 'planter', assigned_probe_channel: 'wh51_ch4', mad_trigger_override: null, note: '' },
    }));
    setNewZoneId('');
  };

  const removeZone = async (zid) => {
    if (!window.confirm(`Remove the per-zone override for ${zid}?`)) return;
    try {
      await axios.delete(`${API_URL}/api/sensors/zone-config/${encodeURIComponent(zid)}`, {
        withCredentials: true,
      });
      const next = { ...overrides };
      delete next[zid];
      setOverrides(next);
      toast.success('Override removed.');
    } catch (e) {
      toast.error('Remove failed.');
    }
  };

  const handleSave = async () => {
    try {
      setSaving(true);
      // Normalize: strip empty fields, convert "__none__" → null, coerce mad to float.
      const cleaned = {};
      Object.entries(overrides).forEach(([zid, v]) => {
        cleaned[zid] = {
          zone_type: v.zone_type || 'turf',
          assigned_probe_channel:
            !v.assigned_probe_channel || v.assigned_probe_channel === '__none__'
              ? null
              : v.assigned_probe_channel,
          mad_trigger_override:
            v.mad_trigger_override === '' || v.mad_trigger_override == null
              ? null
              : Number(v.mad_trigger_override),
          note: v.note || '',
        };
      });
      await axios.post(
        `${API_URL}/api/sensors/zone-config`,
        { zone_overrides: cleaned },
        { withCredentials: true },
      );
      toast.success('Per-zone overrides saved.');
      await fetchAll();
    } catch (e) {
      toast.error(e?.response?.data?.detail || 'Save failed.');
    } finally {
      setSaving(false);
    }
  };

  const zoneOptions = useMemo(() => {
    const taken = new Set(Object.keys(overrides));
    return rachioZones.filter((z) => !taken.has(String(z.id)));
  }, [overrides, rachioZones]);

  if (loading) {
    return (
      <Card data-testid="zone-overrides-card">
        <CardHeader>
          <CardTitle>Per-Zone Overrides</CardTitle>
        </CardHeader>
        <CardContent>
          <div className="text-sm text-muted-foreground">Loading…</div>
        </CardContent>
      </Card>
    );
  }

  const overrideEntries = Object.entries(overrides);

  return (
    <Card data-testid="zone-overrides-card" className="border-emerald-200 dark:border-emerald-900">
      <CardHeader>
        <div className="flex items-start justify-between gap-3 flex-wrap">
          <div>
            <CardTitle className="flex items-center gap-2">
              <Flower2 className="w-5 h-5 text-emerald-600" />
              Per-Zone Overrides
              <Badge variant="secondary" className="text-[10px] uppercase tracking-wide">Phase B · planter unlock</Badge>
            </CardTitle>
            <CardDescription>
              Tag specific Rachio zones (planters, ornamentals, isolated beds) with their own moisture
              probe + MAD trigger. Without an override, a zone uses the lawn-average DRY probe like before.
            </CardDescription>
          </div>
        </div>
      </CardHeader>

      <CardContent className="space-y-5">
        {/* Add a new override */}
        <div className="border rounded-md p-3 bg-muted/30">
          <Label className="text-xs uppercase tracking-wide text-muted-foreground">Add zone override</Label>
          <div className="grid grid-cols-1 md:grid-cols-12 gap-3 mt-2 items-end">
            <div className="md:col-span-9">
              {rachioZones.length > 0 ? (
                <Select value={newZoneId} onValueChange={setNewZoneId}>
                  <SelectTrigger data-testid="zone-overrides-add-select">
                    <SelectValue placeholder="Pick a Rachio zone…" />
                  </SelectTrigger>
                  <SelectContent>
                    {zoneOptions.length === 0 ? (
                      <SelectItem value="__none__" disabled>
                        All zones already configured
                      </SelectItem>
                    ) : (
                      zoneOptions.map((z) => (
                        <SelectItem key={z.id} value={String(z.id)}>
                          {z.name} ({z.id})
                        </SelectItem>
                      ))
                    )}
                  </SelectContent>
                </Select>
              ) : (
                <Input
                  value={newZoneId}
                  onChange={(e) => setNewZoneId(e.target.value)}
                  placeholder="Rachio zone id (e.g. abcd-1234) — connect Rachio to auto-list"
                  data-testid="zone-overrides-add-input"
                />
              )}
            </div>
            <div className="md:col-span-3">
              <Button
                onClick={addZone}
                className="w-full"
                variant="secondary"
                data-testid="zone-overrides-add-button"
              >
                <Plus className="w-4 h-4 mr-1" /> Add override
              </Button>
            </div>
          </div>
        </div>

        {/* List of overrides */}
        {overrideEntries.length === 0 ? (
          <div className="text-sm text-muted-foreground italic" data-testid="zone-overrides-empty">
            No zones overridden. Every Rachio zone currently uses the lawn-average DRY probe and your
            schedule's default MAD trigger.
          </div>
        ) : (
          <div className="space-y-3">
            {overrideEntries.map(([zid, ov]) => {
              const matchedName =
                rachioZones.find((z) => String(z.id) === String(zid))?.name || null;
              const probeVal = ov.assigned_probe_channel || '__none__';
              const typeMeta = ZONE_TYPES.find((t) => t.value === (ov.zone_type || 'turf'));
              const Icon = typeMeta?.icon || Sprout;
              return (
                <div
                  key={zid}
                  className="border rounded-md p-3 bg-card"
                  data-testid={`zone-override-row-${zid}`}
                >
                  <div className="flex items-center justify-between gap-2 mb-3">
                    <div className="flex items-center gap-2">
                      <Icon className="w-4 h-4 text-emerald-600" />
                      <div className="font-mono text-sm font-semibold">{matchedName || zid}</div>
                      {matchedName && (
                        <span className="font-mono text-xs text-muted-foreground">{zid}</span>
                      )}
                    </div>
                    <Button
                      size="sm"
                      variant="ghost"
                      onClick={() => removeZone(zid)}
                      data-testid={`zone-override-remove-${zid}`}
                    >
                      <Trash2 className="w-4 h-4 text-rose-500" />
                    </Button>
                  </div>

                  <div className="grid grid-cols-1 md:grid-cols-12 gap-3">
                    <div className="md:col-span-3">
                      <Label className="text-xs">Zone type</Label>
                      <Select
                        value={ov.zone_type || 'turf'}
                        onValueChange={(v) => updateOverride(zid, 'zone_type', v)}
                      >
                        <SelectTrigger data-testid={`zone-override-type-${zid}`}>
                          <SelectValue />
                        </SelectTrigger>
                        <SelectContent>
                          {ZONE_TYPES.map((t) => (
                            <SelectItem key={t.value} value={t.value}>
                              {t.label} — {t.hint}
                            </SelectItem>
                          ))}
                        </SelectContent>
                      </Select>
                    </div>

                    <div className="md:col-span-4">
                      <Label className="text-xs">Probe channel</Label>
                      <Select
                        value={probeVal}
                        onValueChange={(v) => updateOverride(zid, 'assigned_probe_channel', v)}
                      >
                        <SelectTrigger data-testid={`zone-override-probe-${zid}`}>
                          <SelectValue />
                        </SelectTrigger>
                        <SelectContent>
                          {PROBE_CHANNELS.map((c) => (
                            <SelectItem key={c.value} value={c.value}>
                              {c.label}
                            </SelectItem>
                          ))}
                        </SelectContent>
                      </Select>
                    </div>

                    <div className="md:col-span-2">
                      <Label className="text-xs">MAD trigger %</Label>
                      <Input
                        type="number"
                        min="0"
                        max="100"
                        step="1"
                        value={ov.mad_trigger_override ?? ''}
                        onChange={(e) => updateOverride(zid, 'mad_trigger_override', e.target.value)}
                        placeholder={ov.zone_type === 'planter' ? '30' : ov.zone_type === 'ornamental' ? '50' : 'auto'}
                        data-testid={`zone-override-mad-${zid}`}
                      />
                    </div>

                    <div className="md:col-span-3">
                      <Label className="text-xs">Note</Label>
                      <Input
                        value={ov.note || ''}
                        onChange={(e) => updateOverride(zid, 'note', e.target.value)}
                        placeholder="Front planter, full sun"
                        data-testid={`zone-override-note-${zid}`}
                      />
                    </div>
                  </div>
                </div>
              );
            })}
          </div>
        )}

        <div className="flex justify-end pt-2">
          <Button
            onClick={handleSave}
            disabled={saving}
            data-testid="zone-overrides-save-button"
          >
            {saving ? 'Saving…' : 'Save overrides'}
          </Button>
        </div>
      </CardContent>
    </Card>
  );
};

export default ZoneOverridesCard;
